import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { usePopupState } from 'material-ui-popup-state/hooks';
import { bindPopover, bindTrigger } from 'material-ui-popup-state';
import { isEmpty } from 'lodash';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Pagination,
  Paper,
  Popover,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete,
  Edit,
  Error,
  FilterList,
  Home,
} from '@mui/icons-material';
import Http from '@/services/http';
import { IPagination } from '@/models/pagination-model';
import { IBase } from '@/models/base-model';
// import Popover from '@/components/popover';
import { useDrawer } from '@/contexts/drawer';
import { useDialog } from '@/contexts/dialog';
import Header from '@/components/layouts/header';
import InfoLabel from '@/components/Info/label';
import FilterTags from '@/components/Info/filter-tags';

import BaseFilterForm from './components/base-filter-form';
import UpdateBaseForm from '../update-base';
import CreateBaseForm from '../create-base';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  transition: theme.transitions.create('background', {
    duration: theme.transitions.duration.short,
  }),
  '&:nth-of-type(odd), :hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const ITEMS_PER_PAGE = 50;

const BaseList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [bases, setBases] = useState<IPagination<IBase>>({
    docs: [],
    limit: 0,
    offset: 0,
    totalDocs: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
    pagingCounter: 0,
  });

  const [loadingBases, setLoadingBases] = useState<boolean>(false);

  const submitFilter = useCallback(
    async (values: Record<string, any>) => {
      Object.entries(values).forEach(([key, value]) => {
        let queryValue;
        if (value instanceof Date) {
          queryValue = value.toISOString();
        } else if (!isEmpty(value)) {
          queryValue = value;
          if (typeof value === 'object' && value._id) {
            queryValue = value._id;
          }
        }

        if (typeof value === 'boolean') {
          if (value) queryValue = 'true';
          else queryValue = undefined;
        }

        if (queryValue) {
          searchParams.set(key, queryValue);
        } else {
          searchParams.delete(key);
        }
      });

      if (!values.page) {
        searchParams.delete('page');
      }

      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const fetchBases = useCallback(async () => {
    setLoadingBases(true);

    const { status, data } = await Http.get('bases', {
      params: {
        ...Object.fromEntries(searchParams),
        limit: ITEMS_PER_PAGE,
      },
    });

    if (status === 200) setBases(data);

    setLoadingBases(false);
  }, [searchParams]);

  const drawer = useDrawer();
  const dialog = useDialog();

  const { enqueueSnackbar } = useSnackbar();

  const createBase = () => {
    drawer.open({
      options: {
        width: 400,
      },
      element: <CreateBaseForm />,
      onClose(createdBase) {
        if (createdBase) {
          fetchBases();
        }
      },
    });
  };

  const editBase = (base: IBase) => {
    drawer.open({
      element: <UpdateBaseForm base={base} />,
      onClose(updatedBase) {
        if (updatedBase) fetchBases();
      },
    });
  };

  const deleteBase = (base: IBase) => {
    dialog.open({
      title: 'Confirmação',
      message: `Deseja excluir a base ${base.name}?`,
      buttons: [
        {
          label: 'Não',
        },
        {
          label: 'Sim',
          options: {
            variant: 'outlined',
            color: 'error',
          },
          async callback() {
            const { status } = await Http.delete(`bases/${base._id}`);

            if (status === 200) {
              enqueueSnackbar('Base excluída com sucesso!', {
                variant: 'success',
              });
              await fetchBases();
            } else
              enqueueSnackbar('Ocorreu um erro ao excluir a base!', {
                variant: 'error',
              });
          },
        },
      ],
    });
  };

  useEffect(() => {
    fetchBases();
  }, [fetchBases]);

  const popupState = usePopupState({ variant: 'popover', popupId: 'filter' });

  return (
    <Stack spacing={1}>
      <Header
        breadcrumbs={[
          {
            icon: Home,
          },
          {
            text: 'Configurações',
          },
          {
            text: 'Bases',
          },
        ]}
        headerChildren={
          <Stack alignItems="center" direction="row" spacing={1}>
            <FilterTags
              tagDefs={[
                {
                  paramKey: 'name',
                  valueGetter(value) {
                    return `Nome ${value}`;
                  },
                },
                {
                  paramKey: 'code',
                  valueGetter(value) {
                    return `Código ${value}`;
                  },
                },
                {
                  paramKey: 'active',
                  valueGetter() {
                    return 'Ativa';
                  },
                },
              ]}
            />
            <IconButton color="secondary" {...bindTrigger(popupState)}>
              <FilterList />
            </IconButton>
            <Popover {...bindPopover(popupState)}>
              <BaseFilterForm
                onSubmit={async (values) => {
                  popupState.close();
                  submitFilter(values);
                }}
              />
            </Popover>
            <IconButton
              color="secondary"
              onClick={() => {
                createBase();
              }}
            >
              <AddIcon />
            </IconButton>
          </Stack>
        }
        title="Bases"
      />
      <Paper sx={{ width: '100%' }}>
        <Collapse in={loadingBases} unmountOnExit>
          <Box
            alignItems="center"
            bgcolor="grey.100"
            display="flex"
            justifyContent="center"
            p={4}
          >
            <CircularProgress />
          </Box>
        </Collapse>
        <Collapse in={!loadingBases && bases.docs.length === 0}>
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={2}
            sx={{ p: 2 }}
          >
            <Typography variant="caption">
              <Error sx={{ fontSize: '56px' }} />
            </Typography>
            <Typography color="GrayText" variant="h5">
              Nenhuma base encontrada
            </Typography>
          </Stack>
        </Collapse>
        <Collapse in={!loadingBases || bases.docs.length > 0}>
          <TableContainer sx={{ overflowY: 'auto' }}>
            <Table
              aria-label="bases list"
              sx={{ maxWidth: '100%', whiteSpace: 'nowrap' }}
            >
              <TableBody>
                {bases.docs?.map((base) => (
                  <StyledTableRow key={base._id}>
                    <TableCell sx={{ width: '1%' }}>
                      <Tooltip title="Editar">
                        <IconButton onClick={() => editBase(base)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ width: '1%' }}>
                      <Stack alignItems="center">
                        {base.active ? (
                          <Chip color="success" label="Ativa" />
                        ) : (
                          <Chip color="default" label="Não ativa" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <InfoLabel
                        items={[
                          {
                            title: 'Código',
                            value: base.code || '-',
                          },
                        ]}
                      />
                    </TableCell>
                    <TableCell>
                      <InfoLabel
                        items={[
                          {
                            title: 'Nome',
                            value: base.name || '-',
                          },
                        ]}
                      />
                    </TableCell>
                    <TableCell sx={{ width: '1%' }}>
                      <IconButton onClick={() => deleteBase(base)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
        {bases.totalPages > 1 && (
          <Box sx={{ m: 2 }}>
            <Pagination
              count={bases.totalPages}
              onChange={(event, selectedPage) =>
                submitFilter({ page: selectedPage.toString() })
              }
              page={bases.offset}
            />
          </Box>
        )}
      </Paper>
    </Stack>
  );
};

export default BaseList;
